"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import type { MealInstance } from "@bitebalance/contracts"
import { formatKcal, useI18n } from "@/i18n"
import { MealCard } from "./MealCard"
import { consumptionKey, type MealOutcomeExtras } from "./outcomeDisplay"

export interface WeekGridProps {
  meals: MealInstance[]
  onSwap: (meal: MealInstance) => void
  onMarkOutcome: (meal: MealInstance) => void
  /** Server-authoritative conflicting meal ids (finding C). */
  conflictMealIds?: ReadonlySet<string>
  /** Persisted outcomes from the shared builder (2026-09-03 consistency
   * finding — Plan renders the same state Today does), keyed by
   * `consumptionKey(date, slot)` (ADR-056): a mark belongs to a meal DATE
   * and SLOT, never to a wire meal id that the next revision re-issues. */
  outcomeExtras?: ReadonlyMap<string, MealOutcomeExtras>
  /** T21: the served daily target, so each day in the week column can carry a
   * tick. Already on the page (`profile.calorieTarget`) — nothing new is read,
   * and with no target the ticks simply do not draw. */
  calorieTarget?: number | null
}

const SLOT_ORDER: Record<MealInstance["slot"], number> = {
  breakfast: 0,
  morning_snack: 1,
  lunch: 2,
  afternoon_snack: 3,
  dinner: 4,
  snack: 5,
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Owner redesign 2026-08-30 ("the 7-column wall was a catastrophe"): the
 * week is now a DAY-FOCUSED view — one horizontal day strip (weekday +
 * kcal, today ringed, selection springs) and the selected day's meals as a
 * single comfortable column. One day of information at a time; the strip
 * answers "how does the week distribute?" at a glance without drowning
 * the screen in 30 cards. Same props/behavior contract as the old grid —
 * swap/lock/outcome flows are untouched.
 */
export function WeekGrid({
  meals,
  onSwap,
  onMarkOutcome,
  conflictMealIds,
  outcomeExtras,
  calorieTarget,
}: WeekGridProps) {
  const { t, locale } = useI18n()
  const reduce = useReducedMotion()
  const today = todayIso()
  const days = Array.from(new Set(meals.map(m => m.date))).sort()
  const [selected, setSelected] = useState<string>(() =>
    days.includes(today) ? today : (days[0] ?? today)
  )
  const activeDay = days.includes(selected) ? selected : (days[0] ?? selected)

  const intl = new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE", {
    weekday: "short",
  })
  const intlDay = new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-DE", {
    day: "numeric",
  })

  const dayMeals = meals
    .filter(m => m.date === activeDay)
    .sort((a, b) => SLOT_ORDER[a.slot] - SLOT_ORDER[b.slot])

  const kcalFor = (date: string) =>
    Math.round(meals.filter(m => m.date === date).reduce((sum, m) => sum + m.nutrition.kcal, 0))

  return (
    /* T21 (ADR-070): a horizontal chip strip is a touch pattern. It stays exactly
       that below lg; from lg the week becomes a COLUMN — every day visible at once
       with its kcal against the served target — beside the selected day, because a
       desktop is not a stretched phone. Same data, same interaction contract. */
    <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:items-start lg:gap-8">
      {/* --------------------------------------- Day selector · phone strip */}
      <div
        role="tablist"
        aria-label={t("views.plan.weekTitle")}
        className="flex snap-x gap-2 overflow-x-auto pb-1 lg:hidden"
      >
        {days.map(date => {
          const isToday = date === today
          const isActive = date === activeDay
          const kcal = Math.round(
            meals.filter(m => m.date === date).reduce((sum, m) => sum + m.nutrition.kcal, 0)
          )
          return (
            <button
              key={date}
              role="tab"
              aria-selected={isActive}
              onClick={() => setSelected(date)}
              className={`relative flex min-w-[4.6rem] shrink-0 snap-start flex-col items-center gap-0.5 rounded-bb-lg border px-3 py-2.5 transition-all duration-200 ${
                isActive
                  ? "border-green bg-green text-paper shadow-sm"
                  : "border-line bg-paper text-ink hover:border-green/50"
              } ${isToday && !isActive ? "ring-2 ring-lime/70" : ""}`}
            >
              <span
                className={`text-[10px] font-bold tracking-wide uppercase ${
                  isActive ? "text-lime" : "text-ink-2"
                }`}
              >
                {intl.format(new Date(`${date}T00:00:00Z`))}
              </span>
              <span className="text-lg leading-none font-bold">
                {intlDay.format(new Date(`${date}T00:00:00Z`))}
              </span>
              <span
                className={`text-[10px] ${isActive ? "text-paper/80" : "text-muted-foreground"}`}
              >
                {formatKcal(kcal, locale)}
              </span>
            </button>
          )
        })}
      </div>

      {/* --------------------------------------- Day selector · desktop column */}
      <div
        role="tablist"
        aria-label={t("views.plan.weekTitle")}
        className="hidden flex-col gap-1.5 lg:flex"
      >
        {days.map(date => {
          const isToday = date === today
          const isActive = date === activeDay
          const kcal = kcalFor(date)
          const pct = calorieTarget
            ? Math.max(4, Math.min(100, Math.round((kcal / calorieTarget) * 100)))
            : null
          return (
            <button
              key={date}
              role="tab"
              aria-selected={isActive}
              onClick={() => setSelected(date)}
              className={`flex min-h-11 items-center gap-3 rounded-bb-md border px-3 py-2 text-left transition-all duration-200 ${
                isActive
                  ? "border-green bg-green text-paper shadow-sm"
                  : "border-line bg-paper text-ink hover:border-green/50"
              } ${isToday && !isActive ? "ring-2 ring-lime/70" : ""}`}
            >
              <span className="w-16 shrink-0 text-xs font-bold">
                {intl.format(new Date(`${date}T00:00:00Z`))}{" "}
                {intlDay.format(new Date(`${date}T00:00:00Z`))}
              </span>
              {pct !== null && (
                <span
                  aria-hidden="true"
                  className={`h-1.5 flex-1 overflow-hidden rounded-bb-full ${isActive ? "bg-paper/25" : "bg-ink/10"}`}
                >
                  <span
                    className={`block h-full rounded-bb-full ${isActive ? "bg-lime" : "bg-green-2"}`}
                    style={{ width: `${pct}%` }}
                  />
                </span>
              )}
              <span
                className={`shrink-0 text-xs tabular-nums ${isActive ? "text-paper/80" : "text-muted-foreground"}`}
              >
                {formatKcal(kcal, locale)}
              </span>
            </button>
          )
        })}
      </div>

      {/* ------------------------------------------------- Selected day */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={activeDay}
          initial={reduce ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? undefined : { opacity: 0, y: -8 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="grid max-w-3xl gap-3"
          data-day={activeDay}
        >
          {dayMeals.map(meal => {
            const extras = outcomeExtras?.get(consumptionKey(meal.date, meal.slot)) ?? null
            return (
              <MealCard
                constraintConflict={conflictMealIds?.has(meal.id) ?? false}
                key={meal.id}
                meal={meal}
                loggedOutcome={extras?.outcome ?? null}
                outcomeDetail={extras?.detail ?? null}
                outcomeReplacement={extras?.replacement ?? null}
                onSwap={() => onSwap(meal)}
                onMarkOutcome={() => onMarkOutcome(meal)}
              />
            )
          })}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}
